import { useState, useEffect } from "react";
import { FlaskConical, Briefcase, GitCompareArrows, Info } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const VOORBEELDEN = ["Vormer", "Lijnman", "Chef", "Ovenman", "Hulpmenger", "Afweger", "Deegmaker"];

const statusKleur = (s) =>
  s === "groen" ? "border-emerald-300 bg-emerald-50"
  : s === "geel" ? "border-amber-300 bg-amber-50"
  : s === "verduidelijking_nodig" ? "border-sky-300 bg-sky-50"
  : "border-red-300 bg-red-50";

const ResultBlok = ({ titel, icoon: Icoon, kleurKlasse, r, testid }) => {
  if (!r) return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
      Nog geen uitkomst. Vul de taakvragen in en vergelijk.
    </div>
  );
  const isFg = r.status === "groen" || r.status === "geel";
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${statusKleur(r.status)}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-7 h-7 rounded-lg ${kleurKlasse} flex items-center justify-center`}>
          <Icoon className="w-4 h-4 text-white" />
        </span>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{titel}</span>
      </div>
      {isFg && (
        <div className="space-y-0.5" data-testid={`${testid}-fg`}>
          <p className="text-lg font-bold text-slate-800">Functiegroep {r.functiegroep}</p>
          <p className="text-sm text-slate-600">{r.referentiefunctie} · schaal {r.schaalindicatie}</p>
          <p className="text-xs text-slate-500">Vertrouwen: {r.vertrouwensniveau}</p>
        </div>
      )}
      {r.status === "verduidelijking_nodig" && (
        <div className="space-y-2" data-testid={`${testid}-verduidelijking`}>
          <p className="text-sm text-slate-700">{r.melding}</p>
          {r.opties?.length > 0 && (
            <ul className="text-sm text-slate-700 space-y-1">
              {r.opties.map((o) => (
                <li key={o.id} className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center min-w-[2.2rem] px-1.5 py-0.5 rounded bg-white border border-slate-300 text-xs font-semibold">fg {o.functiegroep}</span>
                  <span>{o.functie || o.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {r.status === "rood" && (
        <p className="text-sm text-red-700" data-testid={`${testid}-rood`}>{r.melding || r.detail || "Onvoldoende informatie voor een indicatie."}</p>
      )}
    </div>
  );
};

export default function FunctiecheckExperiment() {
  const [functienaam, setFunctienaam] = useState("");
  const [afdeling, setAfdeling] = useState("");
  const [uitleg, setUitleg] = useState("");
  const [vragen, setVragen] = useState([]);
  const [antwoorden, setAntwoorden] = useState({});
  const [resultaat, setResultaat] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  useEffect(() => {
    api.post("/functie/orba-experiment", { functienaam: "" })
      .then((res) => {
        setUitleg(res.data.orba_uitleg || "");
        setVragen(res.data.taakvragen || []);
        setAntwoorden(Object.fromEntries((res.data.taakvragen || []).map((v) => [v.id, false])));
      })
      .catch((e) => setFout(e?.response?.data?.detail || "Kon de taakvragen niet laden."));
  }, []);

  const vergelijk = async () => {
    setBezig(true); setFout("");
    try {
      const res = await api.post("/functie/orba-experiment", { functienaam, afdeling: afdeling || null, antwoorden });
      setResultaat(res.data);
    } catch (e) {
      setFout(e?.response?.data?.detail || "Er ging iets mis bij het vergelijken.");
    } finally {
      setBezig(false);
    }
  };

  return (
    <div data-testid="functiecheck-experiment" className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-fuchsia-600 flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-white" />
          </span>
          <h1 className="text-2xl font-bold text-slate-800">Functiecheck vergelijken</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-fuchsia-100 text-fuchsia-700">Experiment</span>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Vergelijk de huidige naam/trefwoord-benadering met de ORBA-taakanalyse (werkzaamheden eerst).
          Alleen voor beheer; verandert de live Functiecheck niet.
        </p>
      </div>

      {uitleg && (
        <div className="zk-card p-4 flex gap-3 bg-sky-50 border-sky-200">
          <Info className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
          <p className="text-sm text-sky-900">{uitleg}</p>
        </div>
      )}

      <div className="zk-card p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="functienaam">Functienaam (zoals op de werkvloer)</Label>
            <Input data-testid="exp-functienaam" id="functienaam" value={functienaam}
              onChange={(e) => setFunctienaam(e.target.value)} placeholder="bv. Vormer, Lijnman, Chef" className="mt-1" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {VOORBEELDEN.map((v) => (
                <button key={v} type="button" data-testid={`exp-voorbeeld-${v.toLowerCase()}`}
                  onClick={() => setFunctienaam(v)}
                  className="text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100">
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="afdeling">Afdeling (optioneel)</Label>
            <Input data-testid="exp-afdeling" id="afdeling" value={afdeling}
              onChange={(e) => setAfdeling(e.target.value)} placeholder="bv. Productie B" className="mt-1" />
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">ORBA-taakvragen (feitelijke werkzaamheden)</p>
          <div className="space-y-2">
            {vragen.map((v) => (
              <label key={v.id} data-testid={`exp-taak-${v.id}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <Checkbox checked={!!antwoorden[v.id]}
                  onCheckedChange={(c) => setAntwoorden((a) => ({ ...a, [v.id]: !!c }))} />
                <span className="text-sm text-slate-700">{v.vraag}</span>
              </label>
            ))}
          </div>
        </div>

        <Button data-testid="exp-vergelijk-btn" onClick={vergelijk} disabled={bezig} className="zk-primary">
          <GitCompareArrows className="w-4 h-4 mr-2" /> {bezig ? "Bezig..." : "Vergelijk huidig vs ORBA"}
        </Button>
        {fout && <p data-testid="exp-fout" className="text-sm text-red-600">{fout}</p>}
      </div>

      {resultaat && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="exp-resultaten">
          <ResultBlok titel="Huidig — naam/trefwoord eerst" icoon={Briefcase} kleurKlasse="bg-amber-500"
            r={resultaat.huidige_resultaat} testid="exp-huidig" />
          <ResultBlok titel="ORBA — werkzaamheden eerst" icoon={FlaskConical} kleurKlasse="bg-fuchsia-600"
            r={resultaat.orba_resultaat} testid="exp-orba" />
        </div>
      )}
    </div>
  );
}
